// Sync de grafo via Yjs.
// Documento Y `graph:<workspaceId>` contém:
//   nodes: Y.Map<nodeId, Y.Map<fields...>>
//   links: Y.Map<linkId, Y.Map<fields...>>
// Mudanças locais (drag, criar nó, etc.) escrevem nos Y.Maps; mudanças remotas
// observam e atualizam o Zustand store.

import * as Y from 'yjs';
import type { GraphLink, GraphNode } from '@synapse/engine';
import { useStore } from '../store/useStore.js';
import { acquireProvider, type SynapseProvider } from './provider.js';
import { setYjsHandle } from './yjsAdapter.js';

interface NodeFields {
  x: number;
  y: number;
  size: number;
  locked: boolean;
  label: string;
  color: string;
  category: 'strategy' | 'idea' | 'execution';
  type: 'idea' | 'text' | 'image' | 'url' | 'universe' | 'doc';
  parentId: string | null;
  icon?: string;
  content?: string;
}

interface LinkFields {
  source: string;
  target: string;
  type: 'synapse' | 'wiki';
  strength: number;
}

function readNodeFields(map: Y.Map<unknown>): NodeFields {
  return {
    x: (map.get('x') as number) ?? 0,
    y: (map.get('y') as number) ?? 0,
    size: (map.get('size') as number) ?? 80,
    locked: (map.get('locked') as boolean) ?? false,
    label: (map.get('label') as string) ?? '',
    color: (map.get('color') as string) ?? '#00f5ff',
    category: (map.get('category') as NodeFields['category']) ?? 'idea',
    type: (map.get('type') as NodeFields['type']) ?? 'idea',
    parentId: (map.get('parentId') as string | null) ?? null,
    icon: (map.get('icon') as string | undefined) ?? undefined,
    content: (map.get('content') as string | undefined) ?? undefined,
  };
}

function nodeFromY(id: string, map: Y.Map<unknown>): GraphNode {
  const f = readNodeFields(map);
  return {
    id,
    x: f.x,
    y: f.y,
    vx: 0,
    vy: 0,
    size: f.size,
    locked: f.locked,
    parentId: f.parentId,
    label: f.label,
    type: f.type,
    category: f.category,
    color: f.color,
    icon: f.icon,
    content: f.content,
  };
}

export interface GraphSyncHandle {
  provider: SynapseProvider;
  ynodes: Y.Map<Y.Map<unknown>>;
  ylinks: Y.Map<Y.Map<unknown>>;
  release: () => void;
  markSynced: () => void;
  // Mutações locais.
  upsertNode: (node: Partial<NodeFields> & { id: string }) => void;
  removeNode: (id: string) => void;
  upsertLink: (link: Partial<LinkFields> & { id: string }) => void;
  removeLink: (id: string) => void;
}

// Tag all local mutations so observeDeep can distinguish them from remote peer changes.
// Remote changes (from Hocuspocus sync) arrive without this origin tag.
const LOCAL_ORIGIN = Symbol('local');

// UUID v4 pattern — rejects legacy non-UUID IDs that may be in corrupt Yjs state.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function attachGraphSync(workspaceId: string): GraphSyncHandle {
  const docName = `graph:${workspaceId}`;
  const provider = acquireProvider(docName);
  const ynodes = provider.doc.getMap<Y.Map<unknown>>('nodes');
  const ylinks = provider.doc.getMap<Y.Map<unknown>>('links');

  // Guard: ignore observeDeep events until after the initial Hocuspocus sync completes.
  // The initial sync fires events with transactionOrigin = provider (not LOCAL_ORIGIN),
  // which would otherwise replace the REST-hydrated store with stale/empty Yjs state.
  // markSynced() is called by useGraphRealtime once the 'synced' event fires.
  let remoteSynced = false;

  // Called only for REMOTE peer changes (another user created/updated/deleted a node).
  // REST hydration and local mutations update the store directly — never via this path.
  function applyRemoteNodes() {
    if (useStore.getState().universeStack.length > 0) return;
    const list: GraphNode[] = [];
    ynodes.forEach((map, id) => {
      if (!UUID_RE.test(id)) return; // Skip corrupt non-UUID nodes from old sessions.
      list.push(nodeFromY(id, map));
    });
    useStore.getState().setNodes(list);
  }

  function applyRemoteLinks() {
    if (useStore.getState().universeStack.length > 0) return;
    const list: GraphLink[] = [];
    ylinks.forEach((map, id) => {
      if (!UUID_RE.test(id)) return;
      const src = (map.get('source') as string) ?? '';
      const tgt = (map.get('target') as string) ?? '';
      if (!UUID_RE.test(src) || !UUID_RE.test(tgt)) return;
      list.push({
        id,
        source: src,
        target: tgt,
        type: ((map.get('type') as string) ?? 'synapse') as GraphLink['type'],
        strength: (map.get('strength') as number) ?? 1,
      });
    });
    useStore.getState().setLinks(list);
  }

  // Skip if every event in the batch came from our own local transaction.
  // Also skip if the initial sync hasn't completed yet (prevent Hocuspocus sync
  // from overwriting REST-hydrated store with server's stale/empty Yjs state).
  const onNodesChange = (events: Y.YEvent<Y.Map<unknown>>[]) => {
    if (!remoteSynced) return;
    if (events.every((e) => e.transaction.origin === LOCAL_ORIGIN)) return;
    applyRemoteNodes();
  };
  const onLinksChange = (events: Y.YEvent<Y.Map<unknown>>[]) => {
    if (!remoteSynced) return;
    if (events.every((e) => e.transaction.origin === LOCAL_ORIGIN)) return;
    applyRemoteLinks();
  };

  ynodes.observeDeep(onNodesChange);
  ylinks.observeDeep(onLinksChange);

  const handle: GraphSyncHandle = {
    provider,
    ynodes,
    ylinks,
    release: () => {
      ynodes.unobserveDeep(onNodesChange);
      ylinks.unobserveDeep(onLinksChange);
      provider.release();
      setYjsHandle(null);
    },
    markSynced: () => { remoteSynced = true; },
    upsertNode(node) {
      provider.doc.transact(() => {
        let map = ynodes.get(node.id);
        if (!map) {
          map = new Y.Map();
          ynodes.set(node.id, map);
        }
        for (const [k, v] of Object.entries(node)) {
          if (k === 'id') continue;
          if (v === undefined) continue;
          map.set(k, v);
        }
      }, LOCAL_ORIGIN);
    },
    removeNode(id) {
      provider.doc.transact(() => {
        ynodes.delete(id);
        ylinks.forEach((map, lid) => {
          if (map.get('source') === id || map.get('target') === id) ylinks.delete(lid);
        });
      }, LOCAL_ORIGIN);
    },
    upsertLink(link) {
      provider.doc.transact(() => {
        let map = ylinks.get(link.id);
        if (!map) {
          map = new Y.Map();
          ylinks.set(link.id, map);
        }
        for (const [k, v] of Object.entries(link)) {
          if (k === 'id') continue;
          if (v === undefined) continue;
          map.set(k, v);
        }
      }, LOCAL_ORIGIN);
    },
    removeLink(id) {
      provider.doc.transact(() => {
        ylinks.delete(id);
      }, LOCAL_ORIGIN);
    },
  };

  setYjsHandle(handle);
  return handle;
}
